import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import Message from '@/components/ui/Message/Message.tsx';
import useCallMessages from '@/api/useCallMessages.ts';
import BotRole from '@/_constants/BotRole.ts';
import { useMemo, useState } from 'react';
import { isSupportedLanguage, SupportedLanguages, SupportedLanguagesArray } from '@/_constants/SupportedLanguages.ts';
import type { SupportedLanguages as SupportedLanguagesT } from '@/_constants/SupportedLanguages.ts';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import dayjs from 'dayjs';
import { cn } from '@/utils/classNames.ts';

type DisplayReservationCommunicationLogModalProps = {
  isOpen: boolean;
  onClose: () => void;
  callLogId: number;
};

function DisplayReservationCommunicationLogModal({
  isOpen,
  onClose,
  callLogId,
}: DisplayReservationCommunicationLogModalProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [locale, setLocale] = useState<SupportedLanguagesT>(
    isSupportedLanguage(language) ? language : SupportedLanguages.ENGLISH
  );

  const request = useMemo(() => ({ callLogId, locale }), [callLogId, locale]);

  const {
    data: messages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
    error: errorMessages,
  } = useCallMessages(request);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{t('modals.displayReservationCommunicationLogModal.title')}</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 empty:hidden mb-2">
            {isErrorMessages && <Alert severity="error">{errorMessages.message}</Alert>}
          </div>
          <div className="pt-2">
            <FormControl>
              <InputLabel shrink id="state">
                {t('label.language')}
              </InputLabel>
              <Select
                value={locale}
                onChange={(event) => setLocale(event.target.value as SupportedLanguagesT)}
                autoWidth
                size="small"
                variant="outlined"
                label={t('label.language')}
                placeholder={t('label.language')}
              >
                {SupportedLanguagesArray.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {t(`languages.${lang}`, { defaultValue: lang })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <LoaderWrapper isLoading={isLoadingMessages} className="flex flex-col gap-4 w-[552px] max-w-full">
            {messages?.map((message) => (
              <div key={message.id} className="flex flex-col gap-1">
                <span
                  className={cn('italic text-xs text-primary-400', {
                    'text-right': message.role === BotRole.USER,
                  })}
                >
                  {dayjs(message.created_at).format('h:mm a | MM/DD/YY')}
                </span>
                <Message message={message.message} isMine={message.role === BotRole.USER} />
              </div>
            ))}
          </LoaderWrapper>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex grow p-4 pt-0 justify-start">
          <BackButton onClick={onClose} autoFocus />
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default DisplayReservationCommunicationLogModal;
